import React, { useState, useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { ColumnConfig, FilterValue } from './types';
import CustomDateRangePicker from '../../common/DatePicker/CustomDateRangePicker';

interface TableFiltersProps {
  columns: ColumnConfig[];
  filters: Record<string, FilterValue>;
  onFiltersChange: (filters: Record<string, FilterValue>) => void;
}

const TableFilters: React.FC<TableFiltersProps> = ({
  columns,
  filters,
  onFiltersChange,
}) => {
  const [localFilters, setLocalFilters] = useState<{
    [x: string]: FilterValue;
  }>(filters);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const filterableColumns = columns
    .filter(col => col.filterable)
    .sort(
      (col1, col2) =>
        (col1?.filterOrder || Infinity) - (col2?.filterOrder || Infinity),
    );

  const handleFilterChange = (
    key: string,
    value: any,
    operator: FilterValue['operator'] = 'contains',
  ) => {
    const newFilters = {
      ...localFilters,
      [key]: { value, operator },
    };
    if (!value) delete newFilters[key];
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  useEffect(() => {
    if (startDate && endDate) {
      const dateString = `${startDate.toISOString().split('T')[0]},${endDate.toISOString().split('T')[0]}`;
      handleFilterChange('transactionDate', dateString, 'between');
    } else {
      handleFilterChange('transactionDate', '', 'between');
    }
  }, [startDate, endDate]);

  if (!filterableColumns.length) return null;

  return (
    <div className="d-flex gap-2 align-items-center">
      <span className="p">Filter</span>

      <Row className="g-3">
        {filterableColumns.map(column => (
          <Col key={column.key} md={4}>
            <Form.Group>
              {column.filterConfig?.type === 'select' ? (
                <Form.Select
                  style={{
                    fontSize: '14px',
                  }}
                  size="lg"
                  className="rounded-2"
                  value={localFilters[column.key]?.value || ''}
                  onChange={e =>
                    handleFilterChange(column.key, e.target.value, 'equals')
                  }
                >
                  <option value="">{column.title}</option>
                  {column.filterConfig.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              ) : column.filterConfig?.type === 'custom' &&
                column.filterConfig.component ? (
                <column.filterConfig.component
                  className="rounded-2"
                  value={localFilters[column.key]?.value}
                  onChange={(value: any) =>
                    handleFilterChange(column.key, value)
                  }
                />
              ) : column.filterConfig?.type === 'daterange' ? (
                <CustomDateRangePicker
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  placeholder={column.filterConfig?.placeholder}
                />
              ) : column.filterConfig?.component ? (
                <column.filterConfig.component
                  className="rounded-2"
                  value={localFilters[column.key]?.value}
                  onChange={(value: any) =>
                    handleFilterChange(column.key, value)
                  }
                />
              ) : (
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  type={column.filterConfig?.type || 'text'}
                  placeholder={`Filter by ${column.title}`}
                  value={localFilters[column.key]?.value || ''}
                  onChange={e => handleFilterChange(column.key, e.target.value)}
                />
              )}
            </Form.Group>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default TableFilters;
